// Lockout screen — replaces the form entirely. Polls /api/lockout for
// server-truth (operator override); ticks the countdown at 1Hz from
// the server-supplied `lockoutUntil` timestamp.

import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import { formatClock, formatRelative } from "../lib/time.js";

export default function LockedState({ initialLockoutUntil, cooldownMin = 15 }) {
  const [lockoutUntil, setLockoutUntil] = useState(initialLockoutUntil);
  const [, setTick] = useState(0);
  const pollTimer = useRef(null);

  useEffect(() => {
    const i = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const res = await api("/lockout", { method: "GET" });
      if (cancelled) return;
      if (res.ok && res.body && !res.body.locked) {
        // Operator cleared the lockout — bounce back to the form.
        window.location.reload();
        return;
      }
      if (res.ok && res.body?.lockoutUntil) {
        setLockoutUntil(res.body.lockoutUntil);
      }
      const next = Math.max(5000, (res.body?.pollAfter ?? Date.now() + 30000) - Date.now());
      pollTimer.current = setTimeout(poll, next);
    };

    pollTimer.current = setTimeout(poll, 10000);
    return () => {
      cancelled = true;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const fallbackUntil = Date.now() + cooldownMin * 60_000;
  const until = lockoutUntil || fallbackUntil;

  return (
    <div className="w-full max-w-md space-y-8">
      <div className="space-y-3">
        <h2 className="font-serif text-display text-aught-ink tracking-tightest">
          Lobby 01 is closed.
        </h2>
        <p className="font-serif text-lg text-aught-ash">
          The door is held. No keys will open it — even the right ones.
        </p>
      </div>

      <div className="hairline pt-6 space-y-2">
        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-aught-ash">
          Reopens at
        </p>
        <p className="font-mono tabular text-3xl text-aught-ink">
          {formatClock(until, { seconds: false })}
        </p>
        <p className="font-mono tabular text-base text-aught-warn">
          in {formatRelative(until)}
        </p>
      </div>

      <div className="hairline pt-6">
        <p className="font-serif italic text-aught-ash text-base">
          If access is needed sooner, set <span className="not-italic font-mono text-[13px] text-aught-ink">LOGIN_LOCK_ENABLED=false</span> in the environment.
        </p>
      </div>

      <p className="font-mono text-[11px] text-aught-ash tabular pt-4">
        aught.app · build {import.meta.env.VITE_BUILD_SHA || "dev"}
      </p>
    </div>
  );
}
